import { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser } from '../services/authService';
import { showErrorToast } from '../services/toastService';
import axios from 'axios';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const logoSrc = '/gymLogo.jpeg';

    const handleLogin = async () => {
        setIsLoading(true);
        try {
            const { token, user } = await loginUser(email, password);
            await login(token, user);
            navigate('/members');
        } catch (error) {
            console.error('Login failed:', error);
            const message = axios.isAxiosError(error)
                ? error.response?.data?.message || error.message
                : error instanceof Error
                    ? error.message
                    : 'Login failed';
            showErrorToast(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-black via-[#1f1f1f] to-[#444242]">
            {/* Animated background elements */}
            <div className="absolute inset-0">
                <div className="absolute top-16 left-12 w-80 h-80 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-16 right-10 w-[28rem] h-[28rem] bg-[#6a6a6a]/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-[#444242]/25 rounded-full blur-2xl animate-pulse delay-500"></div>
            </div>

            {/* Gym equipment silhouettes */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-10 text-6xl">🏋️</div>
                <div className="absolute top-3/4 right-10 text-6xl">💪</div>
                <div className="absolute bottom-1/4 left-1/4 text-4xl">⚡</div>
                <div className="absolute top-1/2 right-1/4 text-4xl">🔥</div>
            </div>

            {/* Main content */}
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-md">
                    {/* Login card with glassmorphism effect */}
                    <div className="backdrop-blur-xl bg-brand-ink/40 border border-white/10 rounded-3xl p-8 shadow-2xl">
                        {/* Header section */}
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-50 h-50 bg-gradient-to-r from-brand-primary to-brand-primaryHover rounded-2xl mb-4 shadow-lg overflow-hidden">
                                <img src={logoSrc} alt="Logo" className="w-full h-full object-cover" />
                            </div>
                            <h1 className="text-3xl font-bold text-[#fbfbfb] mb-2 bg-gradient-to-r from-[#fbfbfb] to-[#6a6a6a] bg-clip-text text-transparent">
                                The Base - Combat System
                            </h1>
                            <p className="text-[#6a6a6a] text-sm">
                                Welcome back! Sign in to manage your fitness empire
                            </p>
                        </div>

                        {/* Login form */}
                        <div className="space-y-6">
                            {/* Email input */}
                            <div className="relative group">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6a6a6a] group-focus-within:text-brand-primary transition-colors" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-[#fbfbfb] placeholder-[#6a6a6a] focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                />
                            </div>

                            {/* Password input */}
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6a6a6a] group-focus-within:text-brand-primary transition-colors" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-[#fbfbfb] placeholder-[#6a6a6a] focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-300 hover:bg-white/10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6a6a6a] hover:text-[#fbfbfb] transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>

                            {/* Login button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                onClick={handleLogin}
                                className="w-full relative overflow-hidden bg-gradient-to-r from-brand-primary to-brand-primaryHover hover:from-brand-primaryHover hover:to-brand-ink text-[#fbfbfb] py-4 rounded-2xl font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed group border border-brand-primary/30"
                            >
                                <div className="flex items-center justify-center space-x-2">
                                    {isLoading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            <span>Signing In...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Sign In</span>
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </div>
                                {/* Button hover effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 opacity-0 group-hover:opacity-30 transition-opacity rounded-2xl"></div>
                            </button>

                            {/* Forgot password link */}
                            <div className="text-center">
                                <a href="#" className="text-[#6a6a6a] hover:text-[#fbfbfb] transition-colors text-sm">
                                    Forgot your password?
                                </a>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="my-8 flex items-center">
                            <div className="flex-1 border-t border-white/10"></div>
                            <span className="px-4 text-[#6a6a6a] text-sm">or</span>
                            <div className="flex-1 border-t border-white/10"></div>
                        </div>

                        {/* Sign up link */}
                        <div className="text-center">
                            <p className="text-[#6a6a6a] text-sm">
                                Don't have an account?{' '}
                                <Link to="/signup" className="text-[#fbfbfb] hover:text-brand-primary transition-colors font-semibold">
                                    Create Account
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Additional info */}
                    <div className="text-center mt-8">
                        <p className="text-[#6a6a6a] text-xs">
                            Secure • Professional • Powerful Gym Management
                        </p>
                    </div>
                </div>
            </div>

            {/* Floating particles effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-2 h-2 bg-white/10 rounded-full animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 2}s`
                        }}
                    ></div>
                ))}
            </div>
        </div>
    );
}
